/**
 * TaskStateStepper — the task's journey, understanding → plan → building → review → check → ready to save
 * (R3 `TaskSnapshot.state` + `transitions`).
 *
 * THE JOURNEY IS A FIXED LINE; STATE IS AN OVERLAY ON IT. `JOURNEY_STEPS` (views.ts) is the backbone.
 * A "waiting for you" state (`needs_input` / `needs_budget` / `amendment_proposed`) is not a tenth
 * step — it has no fixed place in the line, so it renders as a badge on whichever journey step the
 * task was doing when it started waiting (`stepperAnchor`, derived from `transitions`, never guessed
 * from `state` alone). A terminal state (`applied` / `cancelled` / `failed`) is called out on its own,
 * clearly separate from "in progress", because collapsing a `failed` task into a plain progress bar
 * would tell a reader work is still moving when it has stopped.
 *
 * Every state value renders even when this client does not recognise it — `views.ts`'s labellers
 * fall back to the server's own string rather than throwing or going blank, because a newer server
 * shipping a new state must not blank the whole panel out from under an older client.
 */
import { AlertTriangle, Ban, Check, ChevronRight, Clock, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import type { RestingVerdictWords } from "@/services/multi-agent/laneV2/presentation";
import type { TransitionView } from "@/services/multi-agent/laneV2/types";
import { isSideState, isTerminalState, journeyStepsFor, SIDE_STATE_LABEL, stepperAnchor, TERMINAL_STATE_LABEL } from "./views";

export interface TaskStateStepperProps {
  state: string;
  transitions: readonly TransitionView[];
  /** The task rests in `validated` but its latest check failed: the current step shows attention, not a pass. */
  lastCheckFailed?: boolean;
  /** The resting verdict (`restingVerdictOf`): when set and not passed, its words replace the generic banner; only `failed` may say the check failed. */
  verdict?: RestingVerdictWords | null;
  className?: string;
}

export function TaskStateStepper({ state, transitions, lastCheckFailed, verdict, className }: TaskStateStepperProps) {
  const restNote = state === "validated" && verdict && !verdict.passed ? verdict : null;
  const attention = state === "validated" && (!!lastCheckFailed || restNote?.tone === "warn");
  const terminal = isTerminalState(state);
  const waiting = isSideState(state);
  const anchor = stepperAnchor(state, transitions);
  const steps = journeyStepsFor(state, transitions);
  const anchorIndex = steps.findIndex((s) => s.key === anchor);

  return (
    <section aria-labelledby="lanev2-stepper-title" className={cn("rounded-md border border-border bg-card", className)}>
      <header className="px-3.5 py-2.5">
        <h3 id="lanev2-stepper-title" className="text-[13px] font-semibold text-foreground">
          Progress
        </h3>
      </header>

      {terminal ? (
        <div
          className={cn(
            "mx-3.5 mb-3 flex items-center gap-1.5 rounded border px-2.5 py-1.5 text-[12px]",
            state === "applied" ? "border-ma-ok-line text-ma-ok-text" : "border-ma-err-line text-ma-err-text",
          )}
        >
          {state === "applied" ? <Check className="h-3.5 w-3.5 shrink-0" aria-hidden /> : <Ban className="h-3.5 w-3.5 shrink-0" aria-hidden />}
          <span>{TERMINAL_STATE_LABEL[state] ?? state}</span>
        </div>
      ) : null}

      <ol className="flex flex-wrap items-center gap-x-1 gap-y-2 border-t border-border px-3.5 py-3" aria-label="Task journey">
        {steps.map((step, i) => {
          // On a terminal task the whole line reads as "done up to where it stopped" rather than
          // "current" — nothing is still in progress, so nothing gets the pulsing/current treatment.
          const isCurrent = !terminal && i === anchorIndex;
          const isDone = anchorIndex >= 0 ? i < anchorIndex || (terminal && i <= anchorIndex) : false;
          return (
            <li key={step.key} className="flex items-center gap-1">
              <div
                className={cn(
                  "flex items-center gap-1 rounded-full border px-2 py-1 text-[11px] font-medium",
                  isCurrent
                    ? "border-primary/40 bg-primary/10 text-foreground"
                    : isDone
                      ? "border-ma-ok-line text-ma-ok-text"
                      : "border-border text-muted-foreground",
                )}
              >
                {isDone && !isCurrent ? (
                  <Check className="h-3 w-3 shrink-0" aria-hidden />
                ) : isCurrent ? (
                  // A change at rest (checked, waiting for you to save) is not "working": no spinner.
                  attention ? <AlertTriangle className="h-3 w-3 shrink-0 text-ma-warn-text" aria-hidden /> : state === "validated" ? <Check className="h-3 w-3 shrink-0" aria-hidden /> : <Loader2 className="h-3 w-3 shrink-0 animate-spin" aria-hidden />
                ) : null}
                {isCurrent && attention ? "Needs attention" : isCurrent && restNote ? restNote.label : step.label}
              </div>
              {i < steps.length - 1 && <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden />}
            </li>
          );
        })}
      </ol>

      {/*
        The waiting badge sits BELOW the line rather than replacing a step, because it is not a step —
        it is what the task is doing right now, at whichever step it paused on.
      */}
      {waiting && (
        <p
          role="status"
          className="mx-3.5 mb-3 flex items-center gap-1.5 rounded border border-ma-warn-line px-2.5 py-1.5 text-[12px] text-ma-warn-text"
        >
          <Clock className="h-3.5 w-3.5 shrink-0" aria-hidden />
          {SIDE_STATE_LABEL[state] ?? state}
        </p>
      )}

      {state === "validated" && (restNote || lastCheckFailed) && (
        <p
          role="status"
          data-lane-v2-check-failed={restNote && !restNote.failed ? undefined : ""}
          data-lane-v2-rest-verdict={restNote ? restNote.tone : undefined}
          className={cn("mx-3.5 mb-3 flex items-center gap-1.5 rounded border px-2.5 py-1.5 text-[12px]", restNote?.tone === "neutral" ? "border-border text-muted-foreground" : "border-ma-warn-line text-ma-warn-text")}
        >
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
          {restNote ? restNote.words : "The last check failed; you can still save, check again, or ask for changes"}
        </p>
      )}

      {!waiting && !terminal && anchorIndex === -1 && (
        <p className="mx-3.5 mb-3 flex items-center gap-1.5 rounded border border-border px-2.5 py-1.5 text-[12px] text-muted-foreground">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
          {state}
        </p>
      )}

      {transitions.length > 0 && (
        <details className="border-t border-border px-3.5 py-2">
          <summary className="cursor-pointer text-[11px] font-medium text-muted-foreground hover:text-foreground">
            {transitions.length} {transitions.length === 1 ? "transition" : "transitions"}
          </summary>
          <ul className="mt-1.5 space-y-1">
            {transitions.map((t, i) => (
              <li key={`${t.epoch}-${i}`} className="flex flex-wrap items-baseline gap-x-1.5 text-[11px] text-muted-foreground">
                <span className="font-mono">{t.from ?? "(start)"}</span>
                <ChevronRight className="h-2.5 w-2.5 shrink-0" aria-hidden />
                <span className="font-mono text-foreground">{t.to}</span>
                <span>— {t.event}</span>
                {t.reason ? <span>({t.reason})</span> : null}
                <span className="ml-auto shrink-0 tabular-nums">{t.at}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}
